// YERİ: src/app/domains/website/core/tracking-consent.store.ts
//
// Storefront (melbarrel.com) cookie consent + third-party tags.
//
// Flow: the consent banner calls `init()` in the browser. It fetches the PUBLIC
// tag ids (GET /api/web/tracking-config). If consent is disabled by the platform
// the tags load at once; otherwise they load only after "Tümünü kabul et".
// The visitor's choice lives in localStorage 'mb_consent'
// ({ v: 1, analytics, ads, at }) — a per-browser convenience, never sent anywhere.
//
// SSR: every method is a no-op on the server (no window, no storage, no scripts).

import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { API_BASE } from '@/app/core/auth/auth.service';
import { TagLoader, TrackingConfig } from './tag-loader.service';

export const CONSENT_STORAGE_KEY = 'mb_consent';
const CONSENT_VERSION = 1;

export interface ConsentChoice {
  v: number;
  analytics: boolean;
  ads: boolean;
  /** ISO timestamp of the decision. */
  at: string;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

/** Reads the stored choice; anything unreadable or from another version counts as "not decided". */
export function readConsent(storage: Pick<Storage, 'getItem'> | null): ConsentChoice | null {
  try {
    const raw = storage?.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentChoice>;
    if (parsed?.v !== CONSENT_VERSION) return null;
    return {
      v: CONSENT_VERSION,
      analytics: parsed.analytics === true,
      ads: parsed.ads === true,
      at: typeof parsed.at === 'string' ? parsed.at : '',
    };
  } catch {
    return null;
  }
}

export function writeConsent(storage: Pick<Storage, 'setItem'> | null, choice: ConsentChoice): void {
  try {
    storage?.setItem(CONSENT_STORAGE_KEY, JSON.stringify(choice));
  } catch {
    // Private mode / blocked storage: the choice still applies to this page view.
  }
}

@Injectable({ providedIn: 'root' })
export class TrackingConsentStore {
  private http = inject(HttpClient);
  private tags = inject(TagLoader);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private config = signal<TrackingConfig | null>(null);
  private choice = signal<ConsentChoice | null>(null);
  private started = false;

  /** Is there anything to consent to at all? */
  private hasTags = computed(() => {
    const c = this.config();
    return !!c && !!(c.ga4MeasurementId || c.metaPixelId || c.googleAdsTagId);
  });

  /** Banner visibility: tags configured, consent required, no decision yet. */
  readonly bannerVisible = computed(() =>
    this.hasTags() && this.config()!.consentEnabled && this.choice() === null);

  /** Loads the config once and applies a previous decision. Browser only. */
  init(): void {
    if (!this.isBrowser || this.started) return;
    this.started = true;

    this.choice.set(readConsent(this.storage()));

    this.http.get<Envelope<TrackingConfig>>(`${API_BASE}/web/tracking-config`).subscribe({
      next: (r) => {
        const config = r?.data ?? null;
        this.config.set(config);
        if (!config || !this.hasTags()) return;

        if (!config.consentEnabled) {
          // Platform turned consent off: load everything immediately.
          this.tags.load(config, { analytics: true, ads: true });
          return;
        }

        const stored = this.choice();
        if (stored) this.tags.load(config, stored);
      },
      // No config → no tags. The storefront must never break because of analytics.
      error: () => this.config.set(null),
    });
  }

  /**
   * The footer "Cookie preferences" link is shown only when there is something
   * to choose: tags configured and the platform asks for consent.
   */
  readonly canChangePreferences = computed(() => this.hasTags() && !!this.config()?.consentEnabled);

  /** Footer link: show the banner again so the visitor can change the decision. */
  reopen(): void {
    this.choice.set(null);
  }

  acceptAll(): void {
    this.decide(true, true);
  }

  rejectAll(): void {
    this.decide(false, false);
  }

  private decide(analytics: boolean, ads: boolean): void {
    const onceki = readConsent(this.storage());
    const choice: ConsentChoice = { v: CONSENT_VERSION, analytics, ads, at: new Date().toISOString() };
    writeConsent(this.storage(), choice);
    this.choice.set(choice);

    // Withdrawing consent: tags already loaded on this page cannot be unloaded
    // (they keep their cookies and listeners). A reload starts the page again
    // without them — the stored choice now says "no".
    const geriAldi = !!onceki && ((onceki.analytics && !analytics) || (onceki.ads && !ads));
    if (geriAldi && this.isBrowser) {
      window.location.reload();
      return;
    }

    const config = this.config();
    if (config) this.tags.load(config, choice);
  }

  private storage(): Storage | null {
    if (!this.isBrowser) return null;
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  }
}
